/**
 * Data Factory Page Content Repository
 *
 * Centralized content for the Data Factory admin page.
 * Contains all text, metrics definitions, and roadmap phases.
 */

export const navigationItems = [
  { id: 'vision', label: 'Vision', icon: 'eye' },
  { id: 'health', label: 'Data Health', icon: 'activity' },
  { id: 'architecture', label: 'Architecture', icon: 'folder' },
  { id: 'roadmap', label: 'Roadmap', icon: 'map' }
];

export const dataFactoryContent = {
  // ===========================================================================
  // Hero / Page Header
  // ===========================================================================
  hero: {
    title: "Data Factory",
    subtitle: "Building the foundation for AI-powered tutoring insights",
    tagline: "Understand our data landscape, gaps, and the path forward"
  },

  // ===========================================================================
  // Section 1: Vision - The Problem and Why It Matters
  // ===========================================================================
  vision: {
    title: "The Data Vision",
    subtitle: "Understanding the gap between what we have and what we need",

    problem: {
      headline: "The Current Data Gap",
      description: `Every tutoring session generates valuable data—transcripts, recordings, notes, and observations.
        Today, much of this data lives in tutors' personal Google Drives and remains uncollected.
        Without this data, we cannot provide the AI-powered insights that help tutors improve student outcomes.`,

      currentFlow: [
        { step: 1, label: "Tutor conducts session", icon: "video", status: "working" },
        { step: 2, label: "Google Meet auto-saves transcript", icon: "file-text", status: "working" },
        { step: 3, label: "Transcript sits in tutor's Drive", icon: "cloud", status: "gap" },
        { step: 4, label: "Data never reaches our system", icon: "x-circle", status: "gap" },
        { step: 5, label: "No insights generated", icon: "brain", status: "gap" }
      ],

      proposedFlow: [
        { step: 1, label: "Tutor conducts session", icon: "video", status: "working" },
        { step: 2, label: "Google Meet auto-saves transcript", icon: "file-text", status: "working" },
        { step: 3, label: "Auto-sync to Data Factory", icon: "refresh-cw", status: "proposed" },
        { step: 4, label: "AI processes & summarizes", icon: "cpu", status: "proposed" },
        { step: 5, label: "Insights delivered to tutor", icon: "lightbulb", status: "proposed" }
      ]
    },

    gaps: [
      {
        id: 'drive-transcripts',
        title: 'Uncollected Google Drive Transcripts',
        severity: 'critical',
        icon: 'hard-drive',
        description: 'Tutors have auto-generated transcripts from Google Meet stored in their personal Drives. These transcripts contain rich session data but are not being collected.',
        estimatedData: '60-80% of session data',
        impact: 'Cannot track student progress over time or identify learning patterns'
      },
      {
        id: 'unlinked-sessions',
        title: 'Unlinked Session Data',
        severity: 'high',
        icon: 'link-2',
        description: 'Sessions exist in our system but are not linked to students. Without this linkage, we cannot build student learning profiles.',
        estimatedData: 'Varies by tutor',
        impact: 'Student-specific insights are incomplete or unavailable'
      },
      {
        id: 'missing-summaries',
        title: 'Sessions Without AI Analysis',
        severity: 'medium',
        icon: 'file-search',
        description: 'Some transcripts exist but have not been processed by our AI summarization pipeline.',
        estimatedData: 'Depends on sync status',
        impact: 'Tutors miss actionable recommendations for follow-up sessions'
      }
    ],

    whyItMatters: [
      {
        title: 'Tutor Effectiveness',
        icon: 'trending-up',
        description: 'With complete data, we can show tutors which techniques work best for each student, track improvement over time, and suggest personalized strategies.'
      },
      {
        title: 'Student Outcomes',
        icon: 'graduation-cap',
        description: 'Comprehensive session history enables us to identify struggling students early, recognize learning patterns, and ensure no student falls through the cracks.'
      },
      {
        title: 'AI Training & Insights',
        icon: 'brain',
        description: 'More data means better AI. Our summarization and recommendation systems improve with every session we collect, benefiting all tutors and students.'
      },
      {
        title: 'Business Intelligence',
        icon: 'bar-chart-2',
        description: 'Aggregate data reveals which subjects need more support, tutor capacity utilization, and overall program effectiveness—critical for scaling.'
      }
    ]
  },

  // ===========================================================================
  // Section 2: Data Health - Current Metrics
  // ===========================================================================
  health: {
    title: "Current Data Health",
    subtitle: "Real-time metrics from our database and filesystem",

    metricsExplained: {
      total: "All transcripts in our system (synced + manual)",
      linked: "Transcripts connected to a specific student",
      summarized: "Transcripts that have been AI-analyzed",
      coverage: "Students with at least one transcript"
    },

    coverageLegend: [
      { level: 'none', label: 'No Data', color: '#fee2e2', description: 'Student has no linked sessions' },
      { level: 'low', label: '1-2 Sessions', color: '#fef3c7', description: 'Minimal data available' },
      { level: 'medium', label: '3-5 Sessions', color: '#d1fae5', description: 'Moderate data for insights' },
      { level: 'high', label: '6+ Sessions', color: '#10b981', description: 'Rich data for analysis' }
    ]
  },

  // ===========================================================================
  // Section 3: Architecture - Proposed File System
  // ===========================================================================
  architecture: {
    title: "Proposed Data Architecture",
    subtitle: "Student-centric organization with role-based access",

    structureDescription: `The proposed architecture organizes all data around students as the primary entity.
      Each student has their own directory containing profile information, session history organized by date,
      and any assessments. This structure makes it easy to build comprehensive student profiles and enforce
      access control at the student level.`,

    fileStructure: {
      name: 'students/',
      type: 'folder',
      description: 'Root directory for all student data',
      children: [
        {
          name: '[student-uuid]/',
          type: 'folder',
          description: 'Unique folder per student',
          children: [
            { name: 'profile.json', type: 'file', description: 'Student profile, learning style, goals' },
            {
              name: 'sessions/',
              type: 'folder',
              description: 'All sessions organized by date',
              children: [
                {
                  name: '[YYYY-MM-DD]/',
                  type: 'folder',
                  description: 'Sessions from a specific date',
                  children: [
                    { name: 'transcript.json', type: 'file', description: 'Raw transcript content' },
                    { name: 'summary.json', type: 'file', description: 'AI-generated summary' },
                    { name: 'metadata.json', type: 'file', description: 'Session metadata (tutor, duration, etc.)' },
                    { name: 'tutor_notes.md', type: 'file', description: 'Optional tutor observations' }
                  ]
                }
              ]
            },
            {
              name: 'assessments/',
              type: 'folder',
              description: 'Assessment results and analysis',
              children: [
                { name: '[assessment-id].json', type: 'file', description: 'Individual assessment data' }
              ]
            },
            { name: 'learning_trajectory.json', type: 'file', description: 'Progress tracking over time' }
          ]
        }
      ]
    },

    accessControl: {
      title: 'Access Control Model',
      description: 'Data access is controlled by role and tutor-student relationships',
      roles: [
        {
          role: 'Admin',
          icon: 'shield',
          color: '#1F93FE',
          access: 'Full access to all student data across the platform',
          useCase: 'Program oversight, quality assurance, system administration'
        },
        {
          role: 'Team Captain',
          icon: 'users',
          color: '#8b5cf6',
          access: 'Access to students assigned to their team',
          useCase: 'Team management, cross-tutor coordination, escalation handling'
        },
        {
          role: 'Brainiac (Tutor)',
          icon: 'user',
          color: '#10b981',
          access: 'Access only to their assigned students',
          useCase: 'Session preparation, progress tracking, insight review'
        }
      ]
    },

    metadataSchema: {
      title: 'Session Metadata Schema',
      description: 'Standardized structure for session information',
      fields: [
        { name: 'session_id', type: 'uuid', description: 'Unique session identifier' },
        { name: 'student_uuid', type: 'uuid', description: 'Link to student profile' },
        { name: 'tutor_id', type: 'int', description: 'Tutor who conducted the session' },
        { name: 'timestamp', type: 'ISO8601', description: 'Session start time' },
        { name: 'duration_minutes', type: 'int', description: 'Session length' },
        { name: 'source', type: 'enum', description: 'GOOGLE_MEET | MANUAL | WHITEBOARD' },
        { name: 'topics_covered', type: 'array', description: 'Main topics from AI analysis' },
        { name: 'techniques_used', type: 'array', description: 'Neuropedagogy techniques observed' },
        { name: 'engagement_score', type: 'int', description: 'AI-assessed engagement (0-100)' },
        { name: 'follow_up_recommended', type: 'array', description: 'Suggested next steps' }
      ]
    }
  },

  // ===========================================================================
  // Section 4: Roadmap - Implementation Phases
  // ===========================================================================
  roadmap: {
    title: "Implementation Roadmap",
    subtitle: "Phased approach with effort and value analysis",

    phases: [
      {
        id: 1,
        title: 'Data Health Foundation',
        status: 'current',
        effort: 'Medium',
        value: 'High',
        description: 'Build visibility into current data state and identify gaps',
        deliverables: [
          'Data Factory admin dashboard (this page)',
          'Real-time data health metrics API',
          'Student coverage visualization',
          'Gap identification and reporting'
        ],
        dependencies: [],
        businessValue: 'Provides visibility into data gaps, enabling data-driven decisions about where to invest in data collection infrastructure.'
      },
      {
        id: 2,
        title: 'Google Drive Integration',
        status: 'planned',
        effort: 'High',
        value: 'Very High',
        description: 'Automated collection of transcripts from tutor Google Drives',
        deliverables: [
          'OAuth scope expansion for Drive access',
          'Automated transcript discovery service',
          'Batch import pipeline for historical data',
          'Real-time sync for new sessions'
        ],
        dependencies: [1],
        businessValue: 'Unlocks 60-80% of currently uncollected session data. Enables comprehensive student profiles and longitudinal analysis.'
      },
      {
        id: 3,
        title: 'Unified Session Storage',
        status: 'planned',
        effort: 'Medium',
        value: 'High',
        description: 'Migrate to student-centric folder structure with standardized metadata',
        deliverables: [
          'Database schema updates for new structure',
          'Migration scripts for existing data',
          'Unified session retrieval API',
          'Backward compatibility layer'
        ],
        dependencies: [2],
        businessValue: 'Standardized data organization enables faster feature development and more powerful cross-session analysis.'
      },
      {
        id: 4,
        title: 'AI Analysis Pipeline',
        status: 'planned',
        effort: 'High',
        value: 'Very High',
        description: 'Automated analysis and insight generation for all sessions',
        deliverables: [
          'Batch summarization for imported transcripts',
          'Learning trajectory tracking',
          'Automated tutor recommendations',
          'Progress reports for parents/guardians'
        ],
        dependencies: [3],
        businessValue: 'Transforms raw data into actionable insights. Tutors receive personalized recommendations; students get better outcomes.'
      }
    ],

    effortLegend: {
      'Low': { color: '#10b981', description: 'Quick wins, minimal complexity' },
      'Medium': { color: '#f59e0b', description: 'Moderate complexity, phased delivery' },
      'High': { color: '#ef4444', description: 'Significant effort, requires planning' }
    },

    valueLegend: {
      'High': { color: '#10b981', description: 'Significant business impact' },
      'Very High': { color: '#1F93FE', description: 'Transformational capability' }
    }
  }
};

export default dataFactoryContent;
